/* AI brand story cards. Captions/notes and accessible labels come from Liquid translations. */
(() => {
  if (customElements.get('restforward-product-moments')) return;
  class RestForwardProductMoments extends HTMLElement {
    flip(card, automatic = false) {
      const face = card.querySelector('[data-moment-turn]');
      const button = card.querySelector('[data-moment-flip]');
      const front = card.querySelector('[data-moment-front]');
      const back = card.querySelector('[data-moment-back]');
      const flipped = face.getAttribute('aria-pressed') !== 'true';
      const note = back.querySelector('.restforward-moment-rack__note');
      const author = back.querySelector('.restforward-moment-rack__author');
      card.classList.toggle('is-flipped', flipped);
      const description = flipped ? `${note.id} ${author.id}` : front.querySelector('p').id;
      for (const trigger of [face, button]) {
        trigger.setAttribute('aria-pressed', String(flipped));
        trigger.setAttribute('aria-describedby', description);
        trigger.setAttribute('aria-label', flipped ? this.dataset.frontLabel : this.dataset.backLabel);
      }
      front.setAttribute('aria-hidden', String(flipped));
      back.setAttribute('aria-hidden', String(!flipped));
      front.inert = flipped;
      back.inert = !flipped;
      if (!automatic) this.querySelector('[data-moment-status]').textContent = flipped ? `${author.textContent}. ${note.textContent}` : front.querySelector('p').textContent;
    }
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.interacted = false;
      this.timers = [];
      this.imageListeners = [];
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.onInteract = () => { this.interacted = true; this.timers.forEach(clearTimeout); };
      this.rail = this.querySelector('.restforward-moment-rack__rail');
      this.onRailScroll = () => {
        const maximum = Math.max(0, this.rail.scrollWidth - this.rail.clientWidth);
        this.querySelector('[data-moment-step="-1"]').disabled = this.rail.scrollLeft <= 2;
        this.querySelector('[data-moment-step="1"]').disabled = this.rail.scrollLeft >= maximum - 2;
      };
      this.rail.addEventListener('scroll', this.onRailScroll, { passive: true });
      this.rackResize = new ResizeObserver(this.onRailScroll);
      this.rackResize.observe(this.rail);
      this.onRailScroll();
      this.onClick = event => {
        const step = event.target.closest('[data-moment-step]');
        if (step && this.contains(step)) {
          this.onInteract();
          const card = this.querySelector('[data-moment-card]');
          const gap = parseFloat(getComputedStyle(this.rail).gap) || 24;
          this.rail.scrollBy({ left: Number(step.dataset.momentStep) * (card.getBoundingClientRect().width + gap), behavior: this.reduced.matches ? 'instant' : 'smooth' });
          return;
        }
        const trigger = event.target.closest('[data-moment-flip],[data-moment-turn]');
        if (!trigger || !this.contains(trigger)) return;
        this.onInteract();
        this.flip(trigger.closest('[data-moment-card]'));
      };
      this.onKey = event => {
        if (!event.target.matches('[data-moment-turn]') || !['Enter', ' '].includes(event.key)) return;
        event.preventDefault();
        this.onInteract();
        this.flip(event.target.closest('[data-moment-card]'));
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
      this.addEventListener('pointerdown', this.onInteract, { passive: true });
      this.addEventListener('focusin', this.onInteract);
      this.onMotionChange = () => { if (this.reduced.matches) this.timers.forEach(clearTimeout); };
      this.reduced.addEventListener?.('change', this.onMotionChange);
      const reveal = () => {
        if (this.revealed) return;
        this.revealed = true;
        this.classList.add('is-revealed');
        if (this.reduced.matches || this.interacted) return;
        this.querySelectorAll('[data-moment-card]').forEach((card, index) => {
          // Leave odd-numbered cards on their photos; turn cards 2 and 4.
          if (index % 2 === 0) return;
          const schedule = () => {
            if (this.interacted || this.reduced.matches || !this.isConnected) return;
            this.timers.push(setTimeout(() => {
              if (!this.interacted && !this.reduced.matches && this.isConnected) this.flip(card, true);
            }, 1800 + ((index - 1) / 2) * 650));
          };
          const image = card.querySelector('img');
          if (image && !image.complete) {
            image.addEventListener('load', schedule, { once: true });
            this.imageListeners.push([image, schedule]);
          } else schedule();
        });
      };
      if (!('IntersectionObserver' in window)) { reveal(); return; }
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { reveal(); this.observer.disconnect(); }
      }, { threshold: .15 });
      this.observer.observe(this);
    }
    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKey);
      this.removeEventListener('pointerdown', this.onInteract);
      this.removeEventListener('focusin', this.onInteract);
      this.reduced?.removeEventListener?.('change', this.onMotionChange);
      this.observer?.disconnect();
      this.rail?.removeEventListener('scroll', this.onRailScroll);
      this.rackResize?.disconnect();
      this.timers?.forEach(clearTimeout);
      this.imageListeners?.forEach(([image, listener]) => image.removeEventListener('load', listener));
      this.ready = false;
    }
  }
  customElements.define('restforward-product-moments', RestForwardProductMoments);
})();
